import { motion, useReducedMotion } from "framer-motion";
import { DataKeys } from "../../types";
import stage01Img from "../../image/health/Aimma_stage01.png";
import stage02Img from "../../image/health/Aimma_stage02.png";
import stage03Img from "../../image/health/Aimma_stage03.png";
import stage04Img from "../../image/health/Aimma_stage04.png";
import stage05Img from "../../image/health/Aimma_stage05.png";
import { HealthLifecycleStageChip } from "./HealthLifecycleStageChip";
import {
    AimmaProductId,
    getLocalized,
    HealthLang,
    isAimmaProductId,
    LocalizedString,
} from "./healthTypes";
import {
    healthCardBody,
    healthLifecycleChipsRegion,
    healthLifecycleChipReference,
    healthLifecycleDetailCard,
    healthLifecycleDetailCardSticky,
    healthLifecycleJourneyNote,
    healthLifecycleJourneyNotes,
    healthLifecycleStageSceneFrame,
} from "./healthVisualStyles";

type LifecycleStageId =
    | "home-health"
    | "preventive-care"
    | "clinical-diagnosis"
    | "personalized-treatment"
    | "continuous-care";

/** Omics chip hidden on Stage 02 (matches nav); route /health/omics-analysis kept */
const PREVENTIVE_CARE_EXTERNAL_LINKS = [
    {
        label: {
            En: "Whole Genome Analysis",
            Zh: "全基因組分析（WGS）",
            Jp: "全ゲノム分析",
        } as LocalizedString,
        href: "/health/whole-genome-analysis",
    },
] as const;

const STAGE_SCENE_CONFIG: Record<
    LifecycleStageId,
    { imageSrc: string; imageAlt: string }
> = {
    "home-health": {
        imageSrc: stage01Img,
        imageAlt: "Home Health stage visual",
    },
    "preventive-care": {
        imageSrc: stage02Img,
        imageAlt: "Preemptive Consultation stage visual",
    },
    "clinical-diagnosis": {
        imageSrc: stage03Img,
        imageAlt: "Clinical Diagnosis stage visual",
    },
    "personalized-treatment": {
        imageSrc: stage04Img,
        imageAlt: "Personalized Treatment stage visual",
    },
    "continuous-care": {
        imageSrc: stage05Img,
        imageAlt: "Continuous Smart Care stage visual",
    },
};

function resolveStageSceneId(stageId?: string): LifecycleStageId {
    if (stageId && stageId in STAGE_SCENE_CONFIG) {
        return stageId as LifecycleStageId;
    }
    return "home-health";
}

/** Care-setting placeholder — future image slot; not a stage progress bar */
function HealthLifecycleStageSceneVisual({
    stageId,
    className = "",
}: {
    stageId?: string;
    className?: string;
}) {
    const sceneId = resolveStageSceneId(stageId);
    const { imageSrc, imageAlt } = STAGE_SCENE_CONFIG[sceneId];

    return (
        <div className={`${healthLifecycleStageSceneFrame} ${className}`}>
            <img
                src={imageSrc}
                alt={imageAlt}
                className="block h-full w-full object-cover object-center lg:absolute lg:inset-0"
                loading="lazy"
            />
            <div className="sr-only">{imageAlt}</div>
        </div>
    );
}

interface HealthLifecyclePanelProps {
    stage: DataKeys;
    lang: HealthLang;
    transitionDirection?: number;
    onNavigateToModules?: () => void;
    className?: string;
    compactHeader?: boolean;
    /** Pinned sticky-scroll layout — full-height card, no inner scroll */
    stickyScroll?: boolean;
    stageLabel?: string;
}

export function HealthLifecyclePanel({
    stage,
    lang,
    transitionDirection = 1,
    onNavigateToModules,
    className = "",
    compactHeader = false,
    stickyScroll = false,
    stageLabel: stageLabelProp,
}: HealthLifecyclePanelProps) {
    const stepLabel = stageLabelProp ?? getLocalized(stage?.label, lang);
    const title = getLocalized(stage?.title, lang);
    const desc = getLocalized(stage?.desc, lang);
    const stageId = stage?.id as string | undefined;
    const rawVisual = stage?.visualLines;
    let visualLines: string[] = [];
    if (Array.isArray(rawVisual)) {
        visualLines = rawVisual.map((line: DataKeys) =>
            typeof line === "object"
                ? getLocalized(line as LocalizedString, lang)
                : String(line)
        );
    } else if (rawVisual && typeof rawVisual === "object") {
        visualLines = rawVisual[lang] ?? rawVisual.En ?? [];
    }
    const relatedModules: string[] = stage?.relatedModules ?? [];
    const productChips = relatedModules.filter(isAimmaProductId);
    const preventiveCareLinks =
        stageId === "preventive-care" ? PREVENTIVE_CARE_EXTERNAL_LINKS : [];

    const scrollToModules = () => {
        document
            .getElementById("product-suite")
            ?.scrollIntoView({ behavior: "smooth" });
        onNavigateToModules?.();
    };

    const reduceMotion = useReducedMotion();
    const yEnter = transitionDirection > 0 ? 42 : -42;
    const yExit = transitionDirection > 0 ? -42 : 42;

    const headerMb = stickyScroll ? "mb-3" : compactHeader ? "mb-4" : "mb-5 md:mb-6";
    const sceneMb = stickyScroll ? "mb-3" : "mb-4 md:mb-5";

    return (
        <motion.div
            key={stageId ?? stepLabel}
            initial={
                reduceMotion
                    ? { opacity: 1, y: 0, scale: 1 }
                    : { opacity: 0, y: yEnter, scale: 0.985 }
            }
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={
                reduceMotion
                    ? { opacity: 1, y: 0, scale: 1 }
                    : { opacity: 0, y: yExit, scale: 0.985 }
            }
            transition={{ duration: reduceMotion ? 0 : 0.26, ease: [0.22, 1, 0.36, 1] }}
            className={`${healthLifecycleDetailCard} ${
                stickyScroll ? `${healthLifecycleDetailCardSticky} flex flex-col` : ""
            } lg:overflow-hidden lg:!border-0 lg:!rounded-[1.5rem] lg:!p-0 ${className}`}
            role="tabpanel"
        >
            <div className={`text-left [text-align:left] lg:hidden ${headerMb}`}>
                <p className="text-left text-sm font-semibold uppercase tracking-[0.2em] text-teal-500 [text-align:left]">
                    Stage {stepLabel}
                </p>
                <h3 className="mt-2 text-left text-base font-semibold leading-tight tracking-tight text-grey-800 [text-align:left] sm:text-lg">
                    {title}
                </h3>
            </div>

            <div className="w-full lg:flex lg:items-stretch">
                <div className={sceneMb + " lg:mb-0 lg:basis-[60%] lg:shrink-0 lg:self-stretch"}>
                    <HealthLifecycleStageSceneVisual
                        stageId={stageId}
                        className="lg:h-full lg:min-h-[24rem] lg:rounded-none lg:!border-0 lg:!bg-transparent"
                    />
                </div>

                <div className="lg:flex lg:min-w-0 lg:basis-[40%] lg:flex-col lg:justify-start lg:px-5 lg:py-7">
                    <div className={`hidden text-left [text-align:left] lg:block ${headerMb}`}>
                        <p className="text-left text-sm font-semibold uppercase tracking-[0.2em] text-teal-500 [text-align:left]">
                            Stage {stepLabel}
                        </p>
                        <h3 className="mt-2 text-left text-base font-semibold leading-tight tracking-tight text-grey-800 [text-align:left] sm:text-lg lg:text-[1.45rem]">
                            {title}
                        </h3>
                    </div>

                    <p className={`${healthCardBody} text-left text-grey-600 [text-align:left]`}>
                        {desc}
                    </p>

                    {visualLines.length > 0 && (
                        <ul
                            className={`${healthLifecycleJourneyNotes} ${
                                stickyScroll ? "mt-3" : "mt-4 lg:mt-5"
                            }`}
                        >
                            {visualLines.map((line) => (
                                <li key={line} className={healthLifecycleJourneyNote}>
                                    {line}
                                </li>
                            ))}
                        </ul>
                    )}

                    {(preventiveCareLinks.length > 0 || productChips.length > 0) && (
                        <div
                            className={`${healthLifecycleChipsRegion} ${
                                stickyScroll ? "mt-4" : "mt-5 lg:mt-6"
                            }`}
                            role="list"
                            aria-label="Related links and Aimma products"
                        >
                            <div className="flex flex-wrap gap-1.5">
                                {preventiveCareLinks.length > 0
                                    ? preventiveCareLinks.map((item) => (
                                          <a
                                              key={item.href}
                                              href={item.href}
                                              className={healthLifecycleChipReference}
                                          >
                                              {getLocalized(item.label, lang)}
                                          </a>
                                      ))
                                    : productChips.map((productId) => (
                                          <HealthLifecycleStageChip
                                              key={productId}
                                              productId={productId as AimmaProductId}
                                              lang={lang}
                                              onNavigateToModules={scrollToModules}
                                          />
                                      ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </motion.div>
    );
}
